"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import type { TargetPosition } from "@/lib/api/targetPositions";
import type { JobHuntingFile } from "@/lib/api/jobHuntingFiles";
import { JOB_HUNTING_FILE_TYPE_LABELS } from "@/lib/api/jobHuntingFiles";

const MAX_JOB_HUNTING_FILES = 3;

interface Props {
  targetPositions: TargetPosition[];
  jobHuntingFiles: JobHuntingFile[];
  selectedTargetPositionId: string | null;
  selectedJobHuntingFileIds: string[];
  onChangeTargetPosition: (id: string | null) => void;
  onChangeJobHuntingFileIds: (ids: string[]) => void;
  onShowToast: (message: string) => void;
  loadError?: boolean;
}

export default function InterviewContextPicker({
  targetPositions,
  jobHuntingFiles,
  selectedTargetPositionId,
  selectedJobHuntingFileIds,
  onChangeTargetPosition,
  onChangeJobHuntingFileIds,
  onShowToast,
  loadError = false,
}: Props) {
  const router = useRouter();

  // 「ファイルを追加」で増やした「まだ何も選ばれていない空行」のローカルカウンタ。
  // 親 state には空文字列を含めず、選択された時点で親に伝えることで、
  // payload の job_hunting_file_ids に空 ID が混入する事故を避ける。
  const [extraEmptyRowCount, setExtraEmptyRowCount] = useState(0);

  const displayedCount = Math.max(
    selectedJobHuntingFileIds.length + extraEmptyRowCount,
    1
  );

  const canAddMoreRow =
    jobHuntingFiles.length > 0 &&
    displayedCount < MAX_JOB_HUNTING_FILES &&
    extraEmptyRowCount === 0;

  const hasRemovableRows =
    selectedJobHuntingFileIds.length + extraEmptyRowCount > 0;

  const getRowValue = (rowIndex: number) => {
    if (rowIndex < selectedJobHuntingFileIds.length) {
      return selectedJobHuntingFileIds[rowIndex];
    }
    return "";
  };

  const handleRowChange = (rowIndex: number, value: string) => {
    if (
      value !== "" &&
      selectedJobHuntingFileIds.some((id, i) => i !== rowIndex && id === value)
    ) {
      onShowToast("すでに登録されています");
      return;
    }

    if (rowIndex < selectedJobHuntingFileIds.length) {
      const next = [...selectedJobHuntingFileIds];
      if (value === "") {
        next.splice(rowIndex, 1);
      } else {
        next[rowIndex] = value;
      }
      onChangeJobHuntingFileIds(next);
      return;
    }

    if (value !== "") {
      onChangeJobHuntingFileIds([...selectedJobHuntingFileIds, value]);
      setExtraEmptyRowCount((c) => Math.max(c - 1, 0));
    }
  };

  const handleRemoveRow = (rowIndex: number) => {
    if (rowIndex < selectedJobHuntingFileIds.length) {
      onChangeJobHuntingFileIds(
        selectedJobHuntingFileIds.filter((_, i) => i !== rowIndex)
      );
    } else {
      setExtraEmptyRowCount((c) => Math.max(c - 1, 0));
    }
  };

  const handleAddRow = () => {
    if (!canAddMoreRow) return;
    setExtraEmptyRowCount((c) => c + 1);
  };

  return (
    <aside className="bg-surface rounded-lg custom-shadow">
      <div className="p-4 md:p-5 border-b border-gray-200">
        <h2 className="text-lg md:text-xl font-bold">コンテキスト</h2>
      </div>

      <div className="p-4 md:p-5">
        {loadError ? (
          <div className="text-sm text-on-surface bg-pink-100 rounded-xl p-4 leading-relaxed">
            コンテキスト情報を読み込めませんでした。コンテキストなしでも面接は開始できます。
          </div>
        ) : (
          <div className="space-y-5">
            <section>
              <label
                htmlFor="job-hunting-files-row-0"
                className="block text-sm font-bold mb-2"
              >
                応募書類ファイル（任意・複数選択可）
              </label>

              <div className="flex flex-col gap-2">
                {Array.from({ length: displayedCount }, (_, rowIndex) => {
                  const rowId = getRowValue(rowIndex);
                  const keySuffix = rowId || `empty-${rowIndex}`;
                  return (
                    <div key={`row-${keySuffix}-${rowIndex}`} className="flex items-center gap-2">
                      <select
                        id={`job-hunting-files-row-${rowIndex}`}
                        aria-label={`応募書類ファイル ${rowIndex + 1}`}
                        value={rowId}
                        onChange={(e) => handleRowChange(rowIndex, e.target.value)}
                        disabled={jobHuntingFiles.length === 0}
                        className="flex-1 min-w-0 bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-on-surface focus:border-primary focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <option value="">--</option>
                        {jobHuntingFiles.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.title}（{JOB_HUNTING_FILE_TYPE_LABELS[f.file_type]}）
                          </option>
                        ))}
                      </select>

                      {hasRemovableRows && (
                        <button
                          type="button"
                          aria-label="この行を削除"
                          onClick={() => handleRemoveRow(rowIndex)}
                          className="flex-shrink-0 p-2 text-error hover:opacity-70 transition-opacity min-h-[44px] min-w-[44px] flex items-center justify-center"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6" />
                            <path d="M14 11v6" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {canAddMoreRow && (
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddRow}
                    className="bg-white border border-primary text-primary text-xs font-bold rounded px-3 py-1.5 hover:bg-pink-100 transition-colors"
                  >
                    ＋ ファイルを追加
                  </button>
                </div>
              )}
            </section>

            <section>
              <label
                htmlFor="target-position-select"
                className="block text-sm font-bold mb-2"
              >
                面接予定企業（任意・1 社のみ）
              </label>
              <select
                id="target-position-select"
                value={selectedTargetPositionId ?? ""}
                onChange={(e) => onChangeTargetPosition(e.target.value === "" ? null : e.target.value)}
                disabled={targetPositions.length === 0}
                className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-on-surface focus:border-primary focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
              >
                <option value="">指定なし</option>
                {targetPositions.map((tp) => (
                  <option key={tp.id} value={tp.id}>
                    {tp.company_name}
                    {tp.job_title ? `（${tp.job_title}）` : ""}
                  </option>
                ))}
              </select>
            </section>

            <section className="bg-pink-100 rounded-xl p-4">
              <p className="text-sm leading-relaxed mb-3">
                コンテキストを設定しておくと、AI がより精度の高いフィードバックを生成します。
              </p>
              <button
                type="button"
                onClick={() => router.push("/profile")}
                className="w-full bg-white border border-primary text-primary text-sm font-bold rounded-lg py-2.5 hover:bg-pink-100 transition-colors min-h-[44px]"
              >
                プロフィールを編集
              </button>
            </section>
          </div>
        )}
      </div>
    </aside>
  );
}
